/**
 * EmployeeDailyDetail — Pamja ditore mujore per një punëtor
 * Hapet si modal/drawer kur klikohet punëtori te Tab 1 i AttendanceSummaryReport.
 * Një rresht për çdo ditë kalendarike të muajit.
 * Admin mund të korrigjojë çdo ditë (check-in/out/pauzë + arsye).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Stack, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, CircularProgress, Avatar, Alert, LinearProgress,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import EditIcon           from '@mui/icons-material/Edit';
import ChevronLeftIcon    from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon   from '@mui/icons-material/ChevronRight';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import BeachAccessIcon    from '@mui/icons-material/BeachAccess';
import BuildIcon          from '@mui/icons-material/Build';
import TrendingUpIcon     from '@mui/icons-material/TrendingUp';
import TrendingDownIcon   from '@mui/icons-material/TrendingDown';
import DownloadIcon       from '@mui/icons-material/Download';
import * as XLSX          from 'xlsx';
import jsPDF              from 'jspdf';
import autoTable          from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import AttendanceAdjustmentDialog from './AttendanceAdjustmentDialog';
import { getDailyDetail } from '../../services/attendanceDemoService';

import { getAuthHeaders } from '../../services/authSession';
import { API_URL, isDemoMode } from '../../services/backendMode';




function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? (String(iso).slice(0, 5) || '—') : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function fmtMin(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (!m) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function fmtDiff(diffMin) {
  if (diffMin == null || isNaN(diffMin)) return { label: '—', color: 'text.disabled' };
  const m = Math.round(diffMin);
  if (m === 0) return { label: '0m', color: 'text.secondary' };
  const abs = Math.abs(m);
  const h   = Math.floor(abs / 60);
  const mm  = abs % 60;
  const str = h > 0 ? `${h}h ${mm}m` : `${mm}m`;
  return m > 0
    ? { label: `+${str}`, color: 'success.main' }
    : { label: `-${str}`, color: 'error.main' };
}

function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// Status chip per daily row
function DayStatusChip({ row }) {
  const { t } = useTranslation();
  if (!row.is_work_day) {
    return <Chip icon={<BeachAccessIcon />} label={t('report.dayOff')} size="small" color="default" sx={{ opacity: 0.6 }} />;
  }
  const config = {
    absent:         { label: t('report.absentLabel'),  color: 'error' },
    in_progress:    { label: t('report.inProgress'),   color: 'primary' },
    late:           { label: t('report.lateLabel'),    color: 'warning' },
    early_checkout: { label: t('report.earlyCheckout'),color: 'warning' },
    on_time:        { label: t('report.onTime'),       color: 'success' },
  };
  const cfg = config[row.compliance_status] || { label: row.compliance_status || '—', color: 'default' };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip label={cfg.label} color={cfg.color} size="small" />
      {row.is_adjusted && (
        <Tooltip title={`${t('report.correctedBy')} ${row.adj_by_name || 'admin'}: ${row.adj_reason || ''}`}>
          <Chip icon={<BuildIcon />} label={t('report.corrAbbr')} size="small" variant="outlined" color="warning"
            sx={{ cursor: 'help' }} />
        </Tooltip>
      )}
    </Stack>
  );
}

// Summary card
function SumCard({ icon, label, value, color }) {
  return (
    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: `${color}.50`,
      border: '1px solid', borderColor: `${color}.200`, flex: 1, minWidth: 80 }}>
      <Box sx={{ color: `${color}.main`, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h6" fontWeight={700} color={`${color}.main`}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EmployeeDailyDetail({ open, onClose, workerId, workerName, initialYear, initialMonth }) {
  const { t } = useTranslation();
  const today = new Date();
  const [year,    setYear]    = useState(initialYear  || today.getFullYear());
  const [month,   setMonth]   = useState(initialMonth || today.getMonth() + 1);
  const [rows,    setRows]    = useState([]);
  const [worker,  setWorker]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [adjRow,  setAdjRow]  = useState(null); // row being edited

  // Keep year/month in sync with props when dialog opens
  useEffect(() => {
    if (open) {
      setYear(initialYear  || today.getFullYear());
      setMonth(initialMonth || today.getMonth() + 1);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!workerId || !open) return;
    setLoading(true);
    try {
      if (isDemoMode()) {
        const r = getDailyDetail(workerId, year, month);
        setRows(r.data || []);
        setWorker(r.worker || null);
      } else {
        const res = await fetch(
          `${API_URL}/api/reports/attendance-daily?worker_id=${workerId}&year=${year}&month=${month}`,
          { headers: getAuthHeaders() }
        );
        const j = await res.json().catch(() => ({}));
        setRows(j?.data || []);
        setWorker(j?.worker || null);
      }
    } catch (e) {
      console.error('[EmployeeDailyDetail]', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [workerId, year, month, open]);

  useEffect(() => { load(); }, [load]);

  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  }

  // Summary stats from rows
  const stats = (() => {
    const workDays  = rows.filter(r => r.is_work_day);
    const present   = workDays.filter(r => r.check_in && r.compliance_status !== 'absent');
    const absent    = workDays.filter(r => r.compliance_status === 'absent');
    const late      = workDays.filter(r => r.compliance_status === 'late');
    const earlyOut  = workDays.filter(r => r.compliance_status === 'early_checkout');
    const overtime  = workDays.filter(r => Number(r.overtime_minutes) > 0);
    const adjusted  = rows.filter(r => r.is_adjusted);
    const totalWork = workDays.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0);
    const totalPlan = workDays.reduce((s, r) => s + (Number(r.planned_minutes) || 0), 0);
    return { present: present.length, absent: absent.length, late: late.length,
      earlyOut: earlyOut.length, overtime: overtime.length,
      adjusted: adjusted.length, totalWork, totalPlan };
  })();

  // Export Excel
  function exportXlsx() {
    const data = rows.map(r => ({
      [t('report.date')]:            r.date,
      [t('report.dayName')]:         r.day_name,
      [t('report.dayType')]:         r.is_work_day ? t('report.workDay') : t('report.dayOff'),
      [t('report.planned')]:         r.planned_minutes != null ? fmtMin(r.planned_minutes) : '—',
      [t('report.checkIn')]:         fmtTime(r.check_in),
      [`${t('report.breakCol')} (min)`]: r.break_minutes != null ? r.break_minutes : '—',
      [t('report.checkOut')]:        fmtTime(r.check_out),
      [t('report.netHoursCol')]:     fmtMin(r.net_worked_minutes),
      [t('report.difference')]:      fmtDiff(r.diff_minutes).label,
      [t('report.status')]:          r.compliance_status || '—',
      [t('report.corrected')]:       r.is_adjusted ? t('common.yes') : t('common.no'),
      [t('report.corrReason')]:      r.adj_reason || '',
      [t('report.corrBy')]:          r.adj_by_name || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ditore');
    XLSX.writeFile(wb, `attendance-${workerName?.replace(/ /g, '_')}-${year}-${String(month).padStart(2,'0')}.xlsx`);
  }

  function exportPdf() {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text(`${t('report.dailyReport')} — ${workerName} — ${t('report.month_'+month)} ${year}`, 14, 15);
    autoTable(doc, {
      startY: 22,
      head: [[t('report.date'), t('report.dayName'), t('report.planned'), t('report.checkIn'), t('report.breakCol'), t('report.checkOut'), t('report.netHoursCol'), t('report.difference'), t('report.status'), t('report.corrAbbr')]],
      body: rows.map(r => [
        r.date, r.day_name,
        fmtMin(r.planned_minutes),
        fmtTime(r.check_in), fmtMin(r.break_minutes), fmtTime(r.check_out),
        fmtMin(r.net_worked_minutes),
        fmtDiff(r.diff_minutes).label,
        r.compliance_status || '—',
        r.is_adjusted ? r.adj_reason?.slice(0,30) || 'Po' : '',
      ]),
      styles: { fontSize: 6.5 },
      didParseCell: function(data) {
        if (data.section === 'body') {
          const idx = data.row.index;
          const row = rows[idx];
          if (!row) return;
          if (!row.is_work_day)                    data.cell.styles.fillColor = [245, 245, 245];
          else if (row.compliance_status === 'absent') data.cell.styles.fillColor = [255, 235, 238];
          else if (row.compliance_status === 'late')   data.cell.styles.fillColor = [255, 248, 225];
        }
      },
    });
    doc.save(`attendance-${workerName?.replace(/ /g, '_')}-${year}-${String(month).padStart(2,'0')}.pdf`);
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>

        {/* Title bar */}
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>{initials(workerName)}</Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>{workerName}</Typography>
                {worker && (
                  <Typography variant="body2" color="text.secondary">
                    {worker.department_name && `${worker.department_name} · `}
                    {worker.schedule_name && `${worker.schedule_name} `}
                    {worker.start_time && `(${worker.start_time?.slice(0,5)}–${worker.end_time?.slice(0,5)})`}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportXlsx}>Excel</Button>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportPdf}>PDF</Button>
              <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>
          </Stack>

          {/* Month navigation */}
          <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
            <IconButton onClick={prevMonth} size="small"><ChevronLeftIcon /></IconButton>
            <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 140, textAlign: 'center' }}>
              {t('report.month_'+month)} {year}
            </Typography>
            <IconButton onClick={nextMonth} size="small"><ChevronRightIcon /></IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Summary bar */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
              <SumCard icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
                label={t('report.present')}      value={stats.present}   color="success" />
              <SumCard icon={<WarningAmberIcon sx={{ fontSize: 20 }} />}
                label={t('report.absentLabel')}  value={stats.absent}    color="error" />
              <SumCard icon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
                label={t('report.latenessCard')} value={stats.late}      color="warning" />
              <SumCard icon={<TrendingDownIcon sx={{ fontSize: 20 }} />}
                label={t('report.earlyCheckout')} value={stats.earlyOut}  color="warning" />
              <SumCard icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                label={t('report.overtime')}     value={stats.overtime}  color="info" />
              <SumCard icon={<BuildIcon sx={{ fontSize: 20 }} />}
                label={t('report.corrections')}  value={stats.adjusted}  color="secondary" />
              <Box sx={{ flex: 2, minWidth: 180, p: 1.5, borderRadius: 2, bgcolor: 'grey.50',
                border: '1px solid', borderColor: 'grey.200' }}>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">{t('report.monthRealization')}</Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {fmtMin(stats.totalWork)} / {fmtMin(stats.totalPlan)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalPlan > 0 ? Math.min(100, Math.round(stats.totalWork / stats.totalPlan * 100)) : 0}
                  sx={{ height: 8, borderRadius: 4 }}
                  color={stats.totalWork >= stats.totalPlan ? 'success' : 'warning'}
                />
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  {stats.totalPlan > 0
                    ? `${Math.round(stats.totalWork / stats.totalPlan * 100)}% ${t('report.ofPlannedSchedule')}`
                    : t('report.noScheduleAssigned')}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(90vh - 310px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>{t('report.date')}</TableCell>
                    <TableCell sx={{ width: 100 }}>{t('report.dayName')}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>{t('report.planned')}</TableCell>
                    <TableCell sx={{ width: 80 }}>{t('report.checkIn')}</TableCell>
                    <TableCell align="right" sx={{ width: 70 }}>{t('report.breakCol')}</TableCell>
                    <TableCell sx={{ width: 80 }}>{t('report.checkOut')}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>{t('report.netHoursCol')}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>{t('report.difference')}</TableCell>
                    <TableCell>{t('report.status')}</TableCell>
                    <TableCell align="center" sx={{ width: 60 }}>{t('report.corrAbbr')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => {
                    const isToday = row.date === new Date().toISOString().slice(0, 10);
                    const diff    = fmtDiff(row.diff_minutes);
                    const bgColor = !row.is_work_day       ? '#f9f9f9'
                                  : row.compliance_status === 'absent'         ? '#fff5f5'
                                  : row.compliance_status === 'late'           ? '#fffde7'
                                  : row.compliance_status === 'early_checkout' ? '#fff8e1'
                                  : isToday                                   ? '#f0f7ff'
                                  : undefined;
                    return (
                      <TableRow key={row.date || i}
                        sx={{ bgcolor: bgColor, '&:hover': { bgcolor: isToday ? '#e3f2fd' : row.is_work_day ? 'action.hover' : '#f0f0f0' } }}>
                        {/* Date */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={isToday ? 700 : 400} color={isToday ? 'primary.main' : undefined}>
                            {row.date?.slice(5)} {/* MM-DD */}
                          </Typography>
                        </TableCell>
                        {/* Weekday */}
                        <TableCell>
                          <Typography variant="body2" color={!row.is_work_day ? 'text.disabled' : undefined}>
                            {row.day_name}
                          </Typography>
                        </TableCell>
                        {/* Planned */}
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {row.is_work_day && row.planned_minutes ? fmtMin(row.planned_minutes) : '—'}
                          </Typography>
                        </TableCell>
                        {/* Check-in */}
                        <TableCell>
                          <Typography variant="body2"
                            color={row.compliance_status === 'late' ? 'warning.dark' : undefined}>
                            {fmtTime(row.check_in)}
                            {Number(row.late_minutes) > 0 && (
                              <Typography component="span" variant="caption" color="warning.main" ml={0.5}>
                                +{fmtMin(row.late_minutes)}
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        {/* Break */}
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {fmtMin(row.break_minutes)}
                          </Typography>
                        </TableCell>
                        {/* Check-out */}
                        <TableCell>
                          <Typography variant="body2"
                            color={row.compliance_status === 'early_checkout' ? 'warning.dark' : undefined}>
                            {fmtTime(row.check_out)}
                            {Number(row.early_checkout_minutes) > 0 && (
                              <Typography component="span" variant="caption" color="warning.main" ml={0.5}>
                                -{fmtMin(row.early_checkout_minutes)}
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        {/* Net worked */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={row.is_work_day && row.check_in ? 600 : 400}>
                            {fmtMin(row.net_worked_minutes)}
                          </Typography>
                        </TableCell>
                        {/* Diff */}
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.3}>
                            {diff.label !== '—' && (
                              diff.label.startsWith('+')
                                ? <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                : <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
                            )}
                            <Typography variant="body2" color={diff.color} fontWeight={500}>
                              {diff.label}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {/* Status */}
                        <TableCell>
                          {row.is_work_day || row.is_adjusted
                            ? <DayStatusChip row={row} />
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </TableCell>
                        {/* Edit button */}
                        <TableCell align="center">
                          {row.is_work_day && (
                            <Tooltip title={t('report.correctManually')}>
                              <IconButton
                                size="small"
                                onClick={() => setAdjRow({ ...row, worker_id: workerId })}
                                color={row.is_adjusted ? 'warning' : 'default'}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">{t('report.noDataForMonth')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {rows.filter(r => r.is_adjusted).length > 0 &&
              `${rows.filter(r => r.is_adjusted).length} ${t('report.daysWithManualCorrections')}`}
          </Typography>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Adjustment dialog */}
      <AttendanceAdjustmentDialog
        open={!!adjRow}
        onClose={() => setAdjRow(null)}
        row={adjRow}
        workerName={workerName}
        onSaved={() => { setAdjRow(null); load(); }}
      />
    </>
  );
}
